import { useState, useCallback } from 'react';
import axios from 'axios';
import { WizardState, SubmissionStep, SubmissionPlan, isFullFeeGroupData, isSimpleFeeGroupData } from '../types';

export interface UseSubmissionReturn {
  submissionPlan: SubmissionPlan | null;
  isSubmitting: boolean;
  startSubmission: (state: WizardState) => Promise<void>;
  resetSubmission: () => void;
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const useSubmission = (
  onSuccess?: (merchantId: string) => void,
  onError?: (error: string) => void
): UseSubmissionReturn => {
  const [submissionPlan, setSubmissionPlan] = useState<SubmissionPlan | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userRole = localStorage.getItem('userRole')?.toLowerCase();
  const username = localStorage.getItem('username');

  const generateSubmissionPlan = (state: WizardState): SubmissionStep[] => {
    const steps: SubmissionStep[] = [];

    // 1. Merchant
    steps.push({
      id: 'merchant',
      type: 'merchant',
      label: `Creating Merchant: ${state.merchant.name}`,
      dependsOn: [],
      status: 'pending',
    });

    if (state.merchant.type === 'regular') {
      // Regular Merchant: Fee Groups → Subaccounts
      state.subaccounts.forEach((sub, index) => {
        if (sub.feeGroup.mode === 'new') {
          steps.push({
            id: `feegroup-${sub.tempId}`,
            type: 'feeGroup',
            tempId: sub.tempId,
            label: `Creating Fee Group: ${sub.feeGroup.newFeeGroup?.name}`,
            dependsOn: ['merchant'],
            status: 'pending',
          });
        }

        steps.push({
          id: `subaccount-${sub.tempId}`,
          type: 'subaccount',
          tempId: sub.tempId,
          label: `Creating Subaccount: ${sub.name}`,
          dependsOn:
            sub.feeGroup.mode === 'new'
              ? [`feegroup-${sub.tempId}`]
              : ['merchant'],
          status: 'pending',
        });
      });
    } else {
      // Agent Merchant: Fee Groups → Agents → Fee Group Assignments
      state.agents.forEach((agent) => {
        // Fee Groups for this agent
        agent.feeGroupAssignments.forEach(fg => {
          if (fg.feeGroup.mode === 'new') {
            steps.push({
              id: `feegroup-${fg.tempId}`,
              type: 'feeGroup',
              tempId: fg.tempId,
              label: `Creating Fee Group: ${fg.feeGroup.newFeeGroup?.name}`,
              dependsOn: ['merchant'],
              status: 'pending',
            });
          }
        });

        // Agent
        const agentName = `${agent.firstname} ${agent.lastname}`.trim() || 'Agent';
        steps.push({
          id: `agent-${agent.tempId}`,
          type: 'agent',
          tempId: agent.tempId,
          label: `Creating Agent: ${agentName}`,
          dependsOn: ['merchant'],
          status: 'pending',
        });

        // Fee Group Assignments
        agent.feeGroupAssignments.forEach(fg => {
          const deps = [`agent-${agent.tempId}`];
          if (fg.feeGroup.mode === 'new') {
            deps.push(`feegroup-${fg.tempId}`);
          }

          steps.push({
            id: `assignment-${fg.tempId}`,
            type: 'feeGroupAssignment',
            tempId: fg.tempId,
            label: `Assigning ${fg.asset} - ${fg.methodName}`,
            dependsOn: deps,
            status: 'pending',
          });
        });
      });
    }

    return steps;
  };

  const updateStepStatus = (
    stepId: string,
    status: SubmissionStep['status'],
    error?: string,
    createdId?: string
  ) => {
    setSubmissionPlan(prev => {
      if (!prev) return prev;
      const newSteps = prev.steps.map(s =>
        s.id === stepId ? { ...s, status, error, createdId } : s
      );
      const completedSteps = newSteps.filter(s => s.status === 'completed').length;
      const hasError = newSteps.some(s => s.status === 'failed');
      return {
        ...prev,
        steps: newSteps,
        completedSteps,
        isComplete: completedSteps === newSteps.length,
        hasError,
      };
    });
  };

  const startSubmission = useCallback(async (state: WizardState) => {
    setIsSubmitting(true);

    const steps = generateSubmissionPlan(state);
    setSubmissionPlan({
      steps,
      totalSteps: steps.length,
      completedSteps: 0,
      currentStepIndex: 0,
      isComplete: false,
      hasError: false,
    });

    const createdIds: Record<string, string> = {};
    const completedStepIds = new Set<string>();

    try {
      // Execute steps, processing until all are completed
      // Steps are ordered by dependencies, so we process them in order
      while (completedStepIds.size < steps.length) {
        let progressMade = false;

        for (const step of steps) {
          // Skip already completed steps
          if (completedStepIds.has(step.id)) {
            continue;
          }

          // Check if dependencies are met using local tracking
          const dependenciesMet = step.dependsOn.every(depId => completedStepIds.has(depId));

          if (!dependenciesMet) {
            continue;
          }

          progressMade = true;
          updateStepStatus(step.id, 'in_progress');
          await delay(300); // Small delay for UI feedback

          try {
          switch (step.type) {
            case 'merchant': {
              const response = await axios.post(
                process.env.REACT_APP_API_URL + `/api/${userRole}/merchant/action/create-merchant`,
                {
                  name: state.merchant.name,
                  merc_description: state.merchant.description,
                  restricted_ips: state.merchant.restrictedIps,
                  is_batch_postback: state.merchant.isBatchPostback,
                  is_agent_merchant: state.merchant.type === 'agent' ? 'yes' : 'no',
                  username,
                  userRole,
                }
              );

              if (response.data?.status === 'ok') {
                const merchantId = response.data.result?.payload?.id;
                createdIds['merchant'] = merchantId;
                completedStepIds.add(step.id);
                updateStepStatus(step.id, 'completed', undefined, merchantId);
              } else {
                throw new Error(response.data?.message || 'Failed to create merchant');
              }
              break;
            }

            case 'feeGroup': {
              const subaccount = state.subaccounts.find(s => s.tempId === step.tempId);
              const agentFg = state.agents
                .flatMap(a => a.feeGroupAssignments)
                .find(fg => fg.tempId === step.tempId);

              const feeGroupData = subaccount?.feeGroup.newFeeGroup || agentFg?.feeGroup.newFeeGroup;

              if (feeGroupData) {
                let requestBody: any;

                if (isFullFeeGroupData(feeGroupData)) {
                  // Full form data (from subaccounts) - use detailed fee_data
                  const feeDataPayload = feeGroupData.fee_data.map((item) => ({
                    type: item.type,
                    asset: item.asset,
                    asset_type: item.asset_type,
                    fixed: parseFloat(item.fixed) || 0,
                    percentage: parseFloat(item.percentage) || 0,
                    max_amount: parseFloat(item.max_amount) || 0,
                    min_amount: parseFloat(item.min_amount) || 0,
                  }));

                  requestBody = {
                    name: feeGroupData.name,
                    description: feeGroupData.description,
                    fee_data: feeDataPayload,
                    __with_default_feetemplate: true,
                    username,
                    userRole,
                  };
                } else if (isSimpleFeeGroupData(feeGroupData)) {
                  // Simple form data (from agents) - use fixed/percentage only
                  requestBody = {
                    name: feeGroupData.name,
                    description: '',
                    fixed: parseFloat(feeGroupData.fixed) || 0,
                    percentage: parseFloat(feeGroupData.percentage) || 0,
                    __with_default_feetemplate: true,
                    username,
                    userRole,
                  };
                }

                const response = await axios.post(
                  process.env.REACT_APP_API_URL + `/api/${userRole}/feegroup/action/create-feegroup`,
                  requestBody
                );

                if (response.data?.status === 'ok') {
                  const feeGroupId = response.data.result?.payload?.feegroup_id;
                  createdIds[step.id] = feeGroupId;
                  completedStepIds.add(step.id);
                  updateStepStatus(step.id, 'completed', undefined, feeGroupId);
                } else {
                  throw new Error(response.data?.message || 'Failed to create fee group');
                }
              }
              break;
            }

            case 'subaccount': {
              const subaccount = state.subaccounts.find(s => s.tempId === step.tempId);
              if (subaccount) {
                const feeGroupId =
                  subaccount.feeGroup.mode === 'existing'
                    ? subaccount.feeGroup.existingFeeGroupId
                    : createdIds[`feegroup-${subaccount.tempId}`];

                const response = await axios.post(
                  process.env.REACT_APP_API_URL + `/api/${userRole}/subaccount/action/create-subaccount`,
                  {
                    merchant_id: createdIds['merchant'],
                    suba_name: subaccount.name,
                    postback_url: subaccount.postbackUrl,
                    firstname: subaccount.firstname,
                    lastname: subaccount.lastname,
                    mobile_dialcode: subaccount.mobileDialcode,
                    mobile: subaccount.mobile,
                    email: subaccount.email,
                    business_name: subaccount.businessName,
                    feegroup_id: feeGroupId,
                    auth1: subaccount.auth1,
                    auth2: subaccount.auth2,
                    payout_methods: subaccount.paymentMethods,
                    is_masked: subaccount.isMasked,
                    username,
                    userRole,
                  }
                );

                if (response.data?.status === 'ok') {
                  const subaccountId = response.data.result?.payload?.id || response.data.result?.payload?.subaccount_id;
                  createdIds[step.id] = subaccountId;
                  completedStepIds.add(step.id);
                  updateStepStatus(step.id, 'completed', undefined, subaccountId);
                } else {
                  throw new Error(response.data?.message || 'Failed to create subaccount');
                }
              }
              break;
            }


            case 'agent': {
              const agent = state.agents.find(a => a.tempId === step.tempId);
              if (agent) {
                const response = await axios.post(
                  process.env.REACT_APP_API_URL + `/api/${userRole}/agent/action/create-agent`,
                  {
                    firstname: agent.firstname,
                    lastname: agent.lastname,
                    business_name: agent.businessName,
                    email: agent.email,
                    mobile_dialcode: agent.mobileDialcode,
                    mobile: agent.mobile,
                    merchant_id: createdIds['merchant'],
                    username,
                    userRole,
                  }
                );

                if (response.data?.status === 'ok') {
                  const agentId = response.data.result?.payload?.id || response.data.result?.payload?.subaccount_id;
                  createdIds[step.id] = agentId;
                  completedStepIds.add(step.id);
                  updateStepStatus(step.id, 'completed', undefined, agentId);
                } else {
                  throw new Error(response.data?.message || 'Failed to create agent');
                }
              }
              break;
            }

            case 'feeGroupAssignment': {
              const agentFg = state.agents
                .flatMap(a => a.feeGroupAssignments.map(fg => ({ ...fg, agentTempId: a.tempId })))
                .find(fg => fg.tempId === step.tempId);

              if (agentFg) {
                const agentId = createdIds[`agent-${agentFg.agentTempId}`];
                const feeGroupId =
                  agentFg.feeGroup.mode === 'existing'
                    ? agentFg.feeGroup.existingFeeGroupId
                    : createdIds[`feegroup-${agentFg.tempId}`];

                const response = await axios.post(
                  process.env.REACT_APP_API_URL + `/api/${userRole}/agent/action/agent-assign-fee-group`,
                  {
                    name: `${agentFg.asset}-${agentFg.methodName}`,
                    agnt_id: agentId,
                    asset: agentFg.asset,
                    method_id: agentFg.methodId,
                    feegroup_id: feeGroupId,
                    methodName: agentFg.methodName,
                    username,
                    userRole,
                  }
                );

                if (response.data?.status === 'ok') {
                  completedStepIds.add(step.id);
                  updateStepStatus(step.id, 'completed');
                } else {
                  throw new Error(response.data?.message || 'Failed to assign fee group');
                }
              }
              break;
            }
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
          updateStepStatus(step.id, 'failed', errorMessage);
          onError?.(errorMessage);
          setIsSubmitting(false);
          return;
        }
        }

        // Safety check: if no progress was made in this iteration, break to prevent infinite loop
        if (!progressMade) {
          console.error('No progress made in submission loop - possible dependency issue');
          break;
        }
      }

      // All steps completed
      setSubmissionPlan(prev =>
        prev ? { ...prev, isComplete: true } : prev
      );
      onSuccess?.(createdIds['merchant']);
    } catch (error: any) {
      onError?.(error.message || 'Submission failed');
    } finally {
      setIsSubmitting(false);
    }
  }, [userRole, username, onSuccess, onError]);

  const resetSubmission = useCallback(() => {
    setSubmissionPlan(null);
    setIsSubmitting(false);
  }, []);

  return {
    submissionPlan,
    isSubmitting,
    startSubmission,
    resetSubmission,
  };
};

export default useSubmission;
